// src/pages/service-configuration/index.jsx

import React, { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";

// Layout existent
import Header from "../../components/ui/Header";
import Sidebar from "../../components/ui/Sidebar";
import Breadcrumbs from "../../components/ui/Breadcrumbs";
import QuickActionButton from "../../components/ui/QuickActionButton";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";

// Componentele tale UI
import ServiceCard from "./components/ServiceCard";
import ServiceForm from "./components/ServiceForm";
import ServiceTable from "./components/ServiceTable";
import ServiceStats from "./components/ServiceStats";
import OverlayListModal from "../../components/ui/OverlayListModal";
import ServiceFilters from "./components/ServiceFilters";

// Auth context: avem nevoie de business-ul curent ca să refacem lista când se schimbă
import { useAuth } from "../../auth/AuthProvider";

// API pentru servicii (conectat la backend)
import {
  listServices,
  createService,
  updateService,
  deleteService,
} from "../../lib/services";

// API categorii servicii (persistente)
import { listServiceCategories, createServiceCategory } from "../../lib/serviceCategories";

// API staff – pentru lista de personal + patch servicii
import { listStaff, patchStaff } from "../../lib/staff";
import { setServiceConsumables } from "../../lib/inventory";

// === helpers pentru afișare din modelul backend ===
const formatPrice = (row) => {
  if (row?.price_cents != null)
    return (row.price_cents / 100).toFixed(2) + " RON";
  if (row?.price != null) return Number(row.price).toFixed(2) + " RON";
  return "-";
};

const getDuration = (row) => row?.duration_minutes ?? row?.duration ?? "-";

/**
 * Normalizăm răspunsul de la backend astfel încât UI-ul să aibă mereu:
 *  - active  (câmpul real din model)
 *  - is_active (folosit de tabel / statistici / filtre)
 *  - isActive (folosit de carduri / formular)
 *  - duration + duration_minutes
 *  - assignedStaff (lista de staff alocat – pentru UI)
 */
const normalizeService = (row) => {
  if (!row) return row;

  const isActive =
    typeof row.isActive === "boolean"
      ? row.isActive
      : typeof row.is_active === "boolean"
        ? row.is_active
        : typeof row.active === "boolean"
          ? row.active
          : false;

  const duration =
    typeof row.duration !== "undefined"
      ? row.duration
      : row.duration_minutes ?? null;

  const assignedStaff = Array.isArray(row.assigned_staff)
    ? row.assigned_staff
    : Array.isArray(row.assignedStaff)
      ? row.assignedStaff
      : [];

  return {
    ...row,
    active: typeof row.active === "boolean" ? row.active : isActive,
    is_active: typeof row.is_active === "boolean" ? row.is_active : isActive,
    isActive: isActive,
    duration_minutes:
      typeof row.duration_minutes !== "undefined"
        ? row.duration_minutes
        : duration,
    duration,
    assignedStaff,
  };
};

// Component simplu pentru „Categorie nouă"
const CategoryQuickAdd = ({
  onCreate,
  existingCategories,
  categoryPlaceholder,
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;

    try {
      setSaving(true);
      await onCreate(trimmed);
      setName("");
    } finally {
      setSaving(false);
    }
  };

  const placeholder = categoryPlaceholder || t("services.newCategoryPlaceholder");

  return (
    <form onSubmit={handleSubmit} className="mb-6 max-w-xl">
      <Input
        label={t("services.newCategoryLabel")}
        placeholder={placeholder}
        value={name}
        onChange={(e) => setName(e.target.value)}
        description={
          existingCategories.length
            ? t("services.existingCategoriesLabel", { list: existingCategories.join(", ") })
            : t("services.noCategoriesYet")
        }
      />

      <Button
        type="submit"
        variant="default"
        className="mt-3 inline-flex items-center"
        disabled={saving}
      >
        <Icon name="PlusCircle" size={18} className="mr-2" />
        {saving ? t("services.createCategorySaving") : t("services.createCategoryBtn")}
      </Button>
    </form>
  );
};

const ServiceConfiguration = () => {
  const { t } = useTranslation();
  // Sidebar toggle
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  // UI state
  const [viewMode, setViewMode] = useState("table"); // 'table' sau 'cards'
  const [showForm, setShowForm] = useState(false);
  const [editingService, setEditingService] = useState(null);

  // Filtre
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");

  // Date servicii (din backend)
  const [services, setServices] = useState([]);
  const [showServiceListModal, setShowServiceListModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // Staff pentru business (folosit în formularul de serviciu)
  const [staff, setStaff] = useState([]);
  // Categorii persistente (în DB)
  const [serviceCategories, setServiceCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  // Business curent din context (refacem lista când se schimbă)
  const auth = useAuth();
  const { currentBusinessId } = auth;

  const canSeeInventory = Boolean(auth?.permissions?.canSeeInventory);

  // încearcă să găsești obiectul business curent
  const currentBusiness =
    auth.currentBusiness ||
    (Array.isArray(auth.businesses)
      ? auth.businesses.find(
        (b) => String(b.id) === String(currentBusinessId)
      )
      : null);


  // Placeholder din business (tip de afacere)
  const categoryPlaceholder = useMemo(() => {
    if (currentBusiness?.category_placeholder) {
      return currentBusiness.category_placeholder;
    }

    const type = currentBusiness?.business_type;

    if (type === "medical_clinic") {
      return "ex. Consultație, Analize, Control...";
    }
    if (type === "fitness") {
      return "ex. Antrenament personal, Pilates, Yoga...";
    }
    if (type === "beauty") {
      return "ex. Masaj, Coafor, Cosmetică...";
    }

    return "ex. Masaj, Coafor, Cosmetică...";
  }, [currentBusiness]);

  // Breadcrumbs
  const canSeeDashboard = Boolean(auth?.permissions?.canSeeDashboard);
  const breadcrumbItems = canSeeDashboard
    ? [
      { label: t("nav.dashboard"), path: "/app/admin-dashboard" },
      { label: t("services.title"), path: "/app/service-configuration" },
    ]
    : [{ label: t("services.title"), path: "/app/service-configuration" }];

  // Încarcă serviciile din backend
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setErr("");

    listServices({ include_inactive: true })
      .then((data) => {
        if (!alive) return;

        const arr = Array.isArray(data) ? data : [];
        const normalized = arr.map(normalizeService);
        setServices(normalized);
      })
      .catch((e) => {
        if (!alive) return;
        const msg =
          e?.response?.data?.detail ||
          e?.message ||
          "Eroare la încărcarea serviciilor.";
        setErr(msg);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [currentBusinessId]);




  // Încarcă categoriile (persistente) din backend pentru business-ul curent
  useEffect(() => {
    let alive = true;
    setCategoriesLoading(true);

    listServiceCategories()
      .then((data) => {
        if (!alive) return;
        const arr = Array.isArray(data) ? data : [];
        setServiceCategories(arr);
      })
      .catch((e) => {
        if (!alive) return;
        console.error("Nu s-au putut încărca categoriile:", e);
      })
      .finally(() => {
        if (alive) setCategoriesLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [currentBusinessId]);
  // Încarcă staff-ul din backend pentru business-ul curent
  useEffect(() => {
    let alive = true;

    listStaff()
      .then((data) => {
        if (!alive) return;
        const arr = Array.isArray(data) ? data : [];
        setStaff(arr);
      })
      .catch((e) => {
        if (!alive) return;
        console.error("Nu s-a putut încărca staff-ul:", e);
      });

    return () => {
      alive = false;
    };
  }, [currentBusinessId]);

  // Categorii distincte existente (din DB + fallback din servicii)
  const existingCategories = useMemo(() => {
    const fromServices = services
      .map((s) => (s?.category || "").trim())
      .filter((c) => c !== "");

    const fromDb = (serviceCategories || [])
      .map((c) => (c?.name || "").trim())
      .filter(Boolean);

    return Array.from(new Set([...fromDb, ...fromServices]));
  }, [services, serviceCategories]);

  // Filtrare locală în UI (search + category + status)
  const filteredServices = useMemo(() => {
    return services.filter((service) => {
      const matchesSearch =
        (service?.name || service?.title || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        (service?.description || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase());

      const matchesCategory =
        !categoryFilter || (service?.category || "").trim() === categoryFilter;

      const active = !!service?.is_active;
      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "active" && active) ||
        (statusFilter === "inactive" && !active);

      return matchesSearch && matchesCategory && matchesStatus;
    });
  }, [services, searchTerm, categoryFilter, statusFilter]);

  const activeCount = useMemo(() => services.filter((s) => !!s?.is_active).length, [services]);
  const inactiveCount = services.length - activeCount;
  const isDefaultFilters = !searchTerm && !categoryFilter && statusFilter === "active";


  // Toggle sidebar
  const handleToggleSidebar = () => {
    setIsSidebarCollapsed((v) => !v);
  };

  // Deschide formularul pentru creare serviciu
  const handleAddService = () => {
    setEditingService(null);
    setShowForm(true);
  };

  // Deschide formularul pentru editare serviciu
  const handleEditService = (service) => {
    setEditingService(service);
    setShowForm(true);
  };

  // Creează categorie (persistă în DB)
  const handleCreateCategory = async (categoryName) => {
    const trimmed = categoryName.trim();
    if (!trimmed) return;

    try {
      await createServiceCategory({ name: trimmed });
      const data = await listServiceCategories();
      const arr = Array.isArray(data) ? data : [];
      setServiceCategories(arr);
    } catch (e) {
      alert(
        e?.response?.data?.detail ||
        e?.message ||
        "Nu s-a putut crea categoria."
      );
    }
  };

  // === Sync staff <-> serviciu în DB (StaffProfile.services) ===
  // IMPORTANT: facem PATCH-urile SECVENȚIAL, nu cu Promise.all, ca să nu blocăm SQLite.
  const syncServiceStaffAssignments = async (serviceId, assignedStaffIds) => {
    if (!serviceId || !Array.isArray(assignedStaffIds)) return;

    const targetSet = new Set(
      assignedStaffIds
        .map((v) => Number(v))
        .filter((v) => Number.isFinite(v))
    );

    for (const member of staff) {
      const current = Array.isArray(member.services)
        ? member.services
          .map((v) => Number(v))
          .filter((v) => Number.isFinite(v))
        : [];

      let next = current;

      if (targetSet.has(member.id)) {
        // trebuie să fie alocat -> adăugăm serviceId dacă nu există
        if (!current.includes(serviceId)) {
          next = [...current, serviceId];
        }
      } else {
        // NU trebuie să fie alocat -> scoatem serviceId dacă există
        if (current.includes(serviceId)) {
          next = current.filter((id) => id !== serviceId);
        }
      }

      const changed =
        next.length !== current.length ||
        next.some((v, idx) => v !== current[idx]);

      if (!changed) continue;

      try {
        const updated = await patchStaff(member.id, { services: next });

        // actualizăm și în state lista de servicii pentru staff-ul respectiv
        setStaff((prev) =>
          prev.map((s) =>
            s.id === member.id
              ? { ...s, services: updated?.services ?? next }
              : s
          )
        );
      } catch (e) {
        console.error(
          "Nu s-a putut salva serviciile pentru staff",
          member.id,
          e
        );
        // nu întrerupem restul, mergem la următorul
      }
    }
  };

  // Submit din formular (create sau update) – payload + meta.assignedStaffIds
  const handleFormSubmit = async (payload, meta = {}) => {
    const assignedStaffIds = Array.isArray(meta.assignedStaffIds)
      ? meta.assignedStaffIds
      : [];

    const consumables = Array.isArray(meta.consumables) ? meta.consumables : null;

    try {
      let saved;

      if (editingService) {
        saved = await updateService(editingService.id, payload);
      } else {
        saved = await createService(payload);
      }

      // completăm cu staff-ul alocat pentru UI
      const assignedStaffObjects = staff.filter((m) =>
        assignedStaffIds.includes(m.id)
      );

      const norm = normalizeService({
        ...saved,
        assignedStaff: assignedStaffObjects,
      });

      setServices((prev) =>
        editingService
          ? prev.map((s) => (s.id === editingService.id ? norm : s))
          : [norm, ...prev]
      );

      // scriem și în DB relațiile staff <-> serviciu (secvențial)
      await syncServiceStaffAssignments(saved.id, assignedStaffIds);

      // salvează consumabile (ServiceProduct) dacă e activat inventory
      if (canSeeInventory && consumables) {
        try {
          await setServiceConsumables(saved.id, consumables);
        } catch (e) {
          // Nu blocăm salvarea serviciului. Log only (fără pop-up tehnic).
          console.error("Nu s-au putut salva consumabilele:", e);
        }
      }

      setShowForm(false);
      setEditingService(null);
    } catch (e) {
      alert(
        e?.response?.data?.detail ||
        e?.message ||
        "Nu s-a putut salva serviciul."
      );
    }
  };

  // Ștergere serviciu (API + update optimist în UI)
  const handleDeleteService = async (serviceId) => {
    if (!window.confirm("Ești sigur că vrei să ștergi acest serviciu?")) return;

    try {
      await deleteService(serviceId);
      setServices((prev) => prev.filter((s) => s.id !== serviceId));
    } catch (e) {
      const httpStatus = e?.response?.status;

      const apiDetailRaw =
        e?.response?.data?.detail ||
        e?.response?.data?.error ||
        e?.response?.data?.message;

      const apiDetail = typeof apiDetailRaw === "string" ? apiDetailRaw : null;

      // ✅ 409 = are programări → nu poți hard-delete → îl "ștergem" prin dezactivare
      if (httpStatus === 409) {
        try {
          await updateService(serviceId, { active: false });
          setServices((prev) =>
            prev.map((s) =>
              s.id === serviceId
                ? normalizeService({ ...s, active: false, is_active: false, isActive: false })
                : s
            )
          );
          alert(apiDetail || "Serviciul are programări asociate, l-am dezactivat.");
        } catch (e2) {
          alert("Nu pot șterge și nici dezactiva serviciul (eroare la update).");
        }
        return;
      }

      if (httpStatus === 403) {
        alert(apiDetail || "Nu ai permisiuni să ștergi servicii.");
        return;
      }

      alert(apiDetail || e?.message || "Nu s-a putut șterge serviciul.");
    }
  };

  // Toggle activ/inactiv (persistat și în API)
  const handleToggleServiceStatus = async (serviceId) => {
    const row = services.find((s) => s.id === serviceId);
    if (!row) return;

    const nextActive = !row.is_active;
    const next = normalizeService({
      ...row,
      active: nextActive,
      is_active: nextActive,
      isActive: nextActive,
    });

    // update optimist în UI
    setServices((prev) =>
      prev.map((s) => (s.id === serviceId ? next : s))
    );

    try {
      await updateService(serviceId, { active: nextActive });
    } catch (e) {
      // revine dacă a eșuat
      setServices((prev) =>
        prev.map((s) => (s.id === serviceId ? row : s))
      );
      alert(
        e?.response?.data?.detail ||
        e?.message ||
        "Nu s-a putut actualiza statusul."
      );
    }
  };

  // Editări rapide din card (ex. nume, preț, durată)
  const handleQuickEdit = async (serviceId, editData) => {
    const prev = services.find((s) => s.id === serviceId);
    if (!prev) return;

    const apiPayload = {
      name:
        typeof editData.name !== "undefined" ? editData.name : prev.name,
      price:
        typeof editData.price !== "undefined" ? editData.price : prev.price,
      duration_minutes:
        typeof editData.duration !== "undefined"
          ? editData.duration
          : prev.duration_minutes,
    };

    const merged = normalizeService({
      ...prev,
      name: apiPayload.name,
      price: apiPayload.price,
      duration_minutes: apiPayload.duration_minutes,
    });

    // optimist în UI
    setServices((p) =>
      p.map((s) => (s.id === serviceId ? merged : s))
    );

    try {
      await updateService(serviceId, apiPayload);
    } catch (e) {
      // revenire dacă a eșuat
      setServices((p) =>
        p.map((s) => (s.id === serviceId ? prev : s))
      );
      alert(
        e?.response?.data?.detail ||
        e?.message ||
        "Nu s-a putut salva modificarea."
      );
    }
  };

  // Reset filtre
  const handleClearFilters = () => {
    setSearchTerm("");
    setCategoryFilter("");
    setStatusFilter("active");
  };

  return (
    <div className="min-h-screen bg-background">
      <Header
        isCollapsed={isSidebarCollapsed}
        onToggleSidebar={handleToggleSidebar}
      />
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        onToggle={handleToggleSidebar}
      />

      <main
        className={`transition-all duration-300 ease-out-custom page-top pb-20 lg:pb-6 ${isSidebarCollapsed ? "lg:ml-16" : "lg:ml-60"
          }`}
      >
        <div className="p-6">
          {/* Header Section */}
          <div className="mb-8">
            <Breadcrumbs items={breadcrumbItems} className="mb-4" />

            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">
                  {t("services.title")}
                </h1>
                <p className="text-text-secondary">
                  {t("services.pageSubtitle")}
                </p>
              </div>

              <div className="flex items-center space-x-3">
                {/* View Mode Toggle */}
                <div className="flex items-center bg-muted rounded-lg p-1">
                  <Button
                    variant={viewMode === "table" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setViewMode("table")}
                    className="px-3"
                  >
                    <Icon name="Table" size={16} />
                  </Button>
                  <Button
                    variant={viewMode === "cards" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setViewMode("cards")}
                    className="px-3"
                  >
                    <Icon name="Grid3X3" size={16} />
                  </Button>
                </div>

                <Button variant="default" onClick={handleAddService}>
                  <Icon name="Plus" size={18} className="mr-2" />
                  {t("services.addService")}
                </Button>
              </div>
            </div>
          </div>

          {/* Mesaje de eroare / loading */}
          {err && (
            <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {err}
            </div>
          )}
          {loading && (
            <div className="mb-6 text-sm text-text-secondary">Se încarcă…</div>
          )}

          {/* Form Serviciu (SUB BUTON) */}
          {showForm && (
            <div className="mb-8">
              <ServiceForm
                editingService={editingService}
                existingCategories={existingCategories}
                staff={staff}
                onSubmit={handleFormSubmit}
                onCancel={() => {
                  setShowForm(false);
                  setEditingService(null);
                }}
              />
            </div>
          )}

          {/* CATEGORIE NOUĂ – de sine stătător */}
          <CategoryQuickAdd
            onCreate={handleCreateCategory}
            existingCategories={existingCategories}
            categoryPlaceholder={categoryPlaceholder}
          />

          {/* Statistics */}
          <ServiceStats
            services={services}
            canSeeStats={Boolean(auth?.permissions?.canSeeStats)}
            onOpenTotal={() => setShowServiceListModal(true)}
          />

          {/* Filters */}
          <ServiceFilters
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            categories={existingCategories}
            categoryFilter={categoryFilter}
            onCategoryChange={setCategoryFilter}
            statusFilter={statusFilter}
            onStatusChange={setStatusFilter}
            onClearFilters={handleClearFilters}
          />

          {/* Services Display */}
          {!loading &&
            (filteredServices?.length === 0 ? (
              <div className="bg-background border border-border rounded-lg p-12 text-center">
                <Icon
                  name="Package"
                  size={48}
                  className="text-text-secondary mx-auto mb-4"
                />
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  Nu au fost găsite servicii
                </h3>
                <p className="text-text-secondary mb-6">
                  {services.length === 0 && isDefaultFilters
                    ? "Începe prin a adăuga primul serviciu pentru afacerea ta."
                    : isDefaultFilters && activeCount === 0
                      ? `Ai ${inactiveCount} servicii dezactivate. Schimbă statusul pe "Toate" sau "Doar Inactive" ca să le vezi.`
                      : "Încearcă să modifici filtrele pentru a vedea mai multe rezultate."}
                </p>

                {services.length === 0 && isDefaultFilters && (
                  <Button variant="default" onClick={handleAddService}>
                    <Icon name="Plus" size={18} className="mr-2" />
                    Adaugă Primul Serviciu
                  </Button>
                )}

                {services.length > 0 && isDefaultFilters && activeCount === 0 && (
                  <Button variant="outline" onClick={() => setStatusFilter("all")}>
                    <Icon name="Eye" size={18} className="mr-2" />
                    Arată toate serviciile
                  </Button>
                )}
              </div>
            ) : (
              <>
                {viewMode === "table" ? (
                  <ServiceTable
                    services={filteredServices}
                    formatPrice={formatPrice}
                    getDuration={getDuration}
                    onEdit={handleEditService}
                    onDelete={handleDeleteService}
                    onToggleStatus={handleToggleServiceStatus}
                  />
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                    {filteredServices.map((service) => (
                      <ServiceCard
                        key={service?.id}
                        service={service}
                        formatPrice={formatPrice}
                        getDuration={getDuration}
                        onEdit={handleQuickEdit}
                        onDelete={handleDeleteService}
                        onToggleStatus={handleToggleServiceStatus}
                      />
                    ))}
                  </div>
                )}
              </>
            ))}
        </div>
      </main>

      {/* Modal listă servicii (click pe "Total Servicii") */}
      <OverlayListModal
        isOpen={showServiceListModal}
        title={`Servicii (${services?.length || 0})`}
        items={(services || []).map((s) => ({
          id: s.id,
          title: s.name || `Serviciu #${s.id}`,
          subtitle: [s.category, s.is_active ? "activ" : "inactiv"].filter(Boolean).join(" · "),
          meta: [
            typeof s.duration_minutes === "number" ? `${s.duration_minutes} min` : null,
            s.price != null ? formatPrice(s) : null,
          ]
            .filter(Boolean)
            .join(" · "),
        }))}
        onClose={() => setShowServiceListModal(false)}
        onItemClick={(it) => {
          const svc = (services || []).find((x) => String(x.id) === String(it.id));
          setShowServiceListModal(false);
          if (svc) handleEditService(svc);
        }}
      />
    </div>
  );
};

export default ServiceConfiguration;
